import { type InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { fetchApiWithToken } from '~/api/utils'
import type { AddressResponse } from '~/types/global'

export type ContactResponse = {
  id: number
  firstname: string
  lastname: string
  email: string
  phone?: string
  address?: AddressResponse
  archived: boolean
  company: {
    id: number
    name: string
  }
}

type CompanyResponse = {
  id: number
  name: string
  address?: AddressResponse
  contacts?: ContactResponse[]
}

export type CompaniesLDResponse = {
  'hydra:member': CompanyResponse[]
  'hydra:totalItems': number
}

export const fetchCompany = ({
  companyId,
  signal
}: { companyId: number; signal?: AbortSignal }) =>
  fetchApiWithToken<CompanyResponse>({
    url: `/v1/companies/${companyId}`,
    signal
  })

export const useCompany = (companyId?: number) => {
  return useQuery<CompanyResponse>({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) =>
      fetchCompany({ companyId: companyId ?? 0, signal }),
    enabled: !!companyId
  })
}

export const useCompanies = ({
  ids,
  search,
  enabled = true
}: {
  ids?: string[]
  search?: string
  enabled?: boolean
}) => useInfiniteQuery<
  CompaniesLDResponse,
  Error,
  InfiniteData<CompaniesLDResponse>
>({
  queryKey: ['companies', ids, search],
  initialPageParam: 1,
  placeholderData: (prev) => prev,
  getNextPageParam: (lastPage, allPages) => {
    const currentPage = allPages.length
    if (lastPage['hydra:totalItems'] > currentPage * 30) {
      return currentPage + 1
    }
  },
  queryFn: async ({ pageParam, signal }) => {
    const queryParams = new URLSearchParams()
    if (ids) {
      for (const id of ids) {
        queryParams.append('id[]', id.toString())
      }
    }
    if (search) {
      queryParams.append('name', search)
    }
    queryParams.append('order[name]', 'asc')
    return (await fetchApiWithToken({
      method: 'get',
      url: `/v1/companies?${queryParams.toString()}&page=${pageParam}`,
      useLDJson: true,
      signal
    })) as CompaniesLDResponse
  },
  enabled,
  staleTime: 60_000
})
